import { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiErrorDetail, setAuthToken, getAuthToken } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth
  const [company, setCompany] = useState(null);

  useEffect(() => {
    (async () => {
      // Skip /auth/me probe entirely when there's no stored token AND no cookie hint — avoids noisy 401 on first visit.
      if (!getAuthToken() && !document.cookie.includes("access_token")) {
        setUser(false);
        setCompany(null);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user);
        setCompany(data.company);
      } catch (_) {
        setAuthToken(null);
        setUser(false);
        setCompany(null);
      }
    })();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) setAuthToken(data.access_token);
      setUser(data.user);
      const me = await api.get("/auth/me");
      setCompany(me.data.company);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.access_token) setAuthToken(data.access_token);
      setUser(data.user);
      setCompany(data.company);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) { /* ignore */ }
    setAuthToken(null);
    setUser(false);
    setCompany(null);
  };

  const refreshCompany = async () => {
    const me = await api.get("/auth/me");
    setCompany(me.data.company);
  };

  return (
    <AuthContext.Provider value={{ user, company, login, register, logout, refreshCompany }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
